import React, { useState, useEffect } from 'react';
import { 
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, 
  addDays, isSameMonth, isSameDay, subMonths, addMonths,
  startOfDay, endOfDay, addWeeks, subWeeks
} from 'date-fns';
import { SocialPost } from '../../../../types/socialMediaPlanner';
import PlatformIcon from '../../../../components/common/PlatformIcon';

type CalendarViewMode = 'day' | 'week' | 'month';

interface CalendarViewProps {
  posts: SocialPost[];
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  onEditPost: (post: SocialPost) => void;
  onDeletePost: (postId: string) => void;
  onReschedulePost?: (postId: string, newDate: Date) => void;
}

/**
 * Calendar View Component
 * Displays posts in a monthly calendar format
 */
const CalendarView: React.FC<CalendarViewProps> = ({
  posts,
  selectedDate,
  onSelectDate,
  onEditPost,
  onDeletePost,
  onReschedulePost
}) => {
  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date()));
  const [currentDay, setCurrentDay] = useState(new Date());
  const [selectedDatePosts, setSelectedDatePosts] = useState<SocialPost[]>([]);
  const [draggedPost, setDraggedPost] = useState<SocialPost | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // Get posts for selected date
  useEffect(() => {
    const filteredPosts = posts.filter(post => {
      if (!post.scheduled_for) return false;
      const postDate = new Date(post.scheduled_for);
      return isSameDay(postDate, selectedDate);
    });
    setSelectedDatePosts(filteredPosts);
  }, [selectedDate, posts]);
  
  // Drag event handlers
  const handleDragStart = (post: SocialPost) => (e: React.DragEvent) => {
    setDraggedPost(post);
    setIsDragging(true);
    
    // Set the drag image and data
    if (e.dataTransfer) {
      e.dataTransfer.setData('application/json', JSON.stringify({ postId: post.id }));
      e.dataTransfer.effectAllowed = 'move';
      
      // Optional: Create a custom drag image
      const dragPreview = document.createElement('div');
      dragPreview.classList.add('bg-white', 'p-2', 'rounded', 'shadow', 'text-sm');
      dragPreview.textContent = post.content?.substring(0, 30) || 'Post';
      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, 20, 20);
      
      // Remove the element after drag starts
      setTimeout(() => {
        document.body.removeChild(dragPreview);
      }, 0);
    }
  };
  
  const handleDragEnd = () => {
    setDraggedPost(null);
    setIsDragging(false);
  };
  
  const handleDragOver = (date: Date) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDrop = (date: Date) => (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedPost || !draggedPost.id || !onReschedulePost) return;
    
    // Preserve the original time when moving to a new date
    let newDate: Date;
    
    if (draggedPost.scheduled_for) {
      const originalDate = new Date(draggedPost.scheduled_for);
      newDate = new Date(date);
      newDate.setHours(
        originalDate.getHours(),
        originalDate.getMinutes(),
        originalDate.getSeconds()
      );
    } else {
      // If no previous scheduled time, set to current time
      newDate = new Date(date);
      const now = new Date();
      newDate.setHours(now.getHours(), now.getMinutes(), 0);
    }
    
    onReschedulePost(draggedPost.id, newDate);
    setDraggedPost(null);
    setIsDragging(false);
  };

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  // Using shared PlatformIcon component instead of local function

  // Format status for display
  const formatStatus = (status: string) => {
    switch (status) {
      case 'draft': return <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">Draft</span>;
      case 'scheduled': return <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">Scheduled</span>;
      case 'published': return <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Published</span>;
      case 'failed': return <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">Failed</span>;
      default: return null;
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode: CalendarViewMode) => {
    setViewMode(mode);
  };

  // Navigate to previous period (day, week, or month)
  const prevPeriod = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDay(addDays(currentDay, -1));
        break;
      case 'week':
        setCurrentWeek(subWeeks(currentWeek, 1));
        break;
      case 'month':
        setCurrentMonth(subMonths(currentMonth, 1));
        break;
    }
  };

  // Navigate to next period (day, week, or month)
  const nextPeriod = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDay(addDays(currentDay, 1));
        break;
      case 'week':
        setCurrentWeek(addWeeks(currentWeek, 1));
        break;
      case 'month':
        setCurrentMonth(addMonths(currentMonth, 1));
        break;
    }
  };

  // Get current period label based on view mode
  const getCurrentPeriodLabel = () => {
    switch (viewMode) {
      case 'day':
        return format(currentDay, 'MMMM d, yyyy');
      case 'week':
        return `${format(currentWeek, 'MMM d')} - ${format(addDays(currentWeek, 6), 'MMM d, yyyy')}`;
      case 'month':
        return format(currentMonth, 'MMMM yyyy');
      default:
        return '';
    }
  };

  // Render calendar header
  const renderHeader = () => {
    return (
      <div className="mb-4 space-y-2">
        <div className="flex justify-between items-center">
          <button onClick={prevPeriod} className="p-2 hover:bg-gray-200 rounded-full">
            <div className="w-5 h-5 flex items-center justify-center">
              <div className="w-3 h-3 border-t border-l border-gray-600 transform -rotate-45"></div>
            </div>
          </button>
          <h2 className="font-bold text-xl">
            {getCurrentPeriodLabel()}
          </h2>
          <button onClick={nextPeriod} className="p-2 hover:bg-gray-200 rounded-full">
            <div className="w-5 h-5 flex items-center justify-center">
              <div className="w-3 h-3 border-t border-r border-gray-600 transform rotate-45"></div>
            </div>
          </button>
        </div>
        
        {/* View Mode Selector */}
        <div className="flex justify-center">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border ${viewMode === 'day' ? 'bg-blue-50 text-blue-600 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} rounded-l-lg`}
              onClick={() => handleViewModeChange('day')}
            >
              Day
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border-t border-b ${viewMode === 'week' ? 'bg-blue-50 text-blue-600 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} border-gray-300`}
              onClick={() => handleViewModeChange('week')}
            >
              Week
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border ${viewMode === 'month' ? 'bg-blue-50 text-blue-600 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} rounded-r-lg`}
              onClick={() => handleViewModeChange('month')}
            >
              Month
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render days of the week
  const renderDays = () => {
    const days = [];
    const dateFormat = "EEEE";
    let startDate = startOfWeek(new Date());

    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="text-center font-medium py-2 border-b">
          {format(addDays(startDate, i), dateFormat).substring(0, 3)}
        </div>
      );
    }

    return <div className="grid grid-cols-7 gap-2">{days}</div>;
  };

  // Get posts for a specific date
  const getPostsForDate = (date: Date): SocialPost[] => {
    return posts.filter(post => {
      if (!post.scheduled_for) return false;
      const postDate = new Date(post.scheduled_for);
      return isSameDay(postDate, date);
    });
  };

  // Color utility functions
  const hexToRgb = (hex: string): { r: number, g: number, b: number } | null => {
    // Remove the hash if it exists
    hex = hex.replace(/^#/, '');
    
    // Parse the hex values
    if (hex.length === 3) {
      // Convert shorthand hex to full form
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;
    
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    };
  };

  const darkenColor = (hex: string, percent: number): string => {
    if (!hex.startsWith('#')) hex = '#' + hex;
    
    const rgb = hexToRgb(hex);
    if (!rgb) return hex; // Return original if invalid
    
    const factor = 1 - percent / 100;
    
    // Darken each channel
    const r = Math.floor(rgb.r * factor);
    const g = Math.floor(rgb.g * factor);
    const b = Math.floor(rgb.b * factor);
    
    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  };

  const getContrastTextColor = (hex: string): string => {
    if (!hex.startsWith('#')) hex = '#' + hex;
    
    const rgb = hexToRgb(hex);
    if (!rgb) return '#000000';
    
    // Calculate luminance (perceived brightness)
    // Formula: (0.299*R + 0.587*G + 0.114*B)
    const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
    
    // Use white text for dark backgrounds, black text for light backgrounds
    return luminance > 128 ? '#000000' : '#FFFFFF';
  };

  // Get posts for a specific hour on a date
  const getPostsForHour = (date: Date, hour: number) => {
    return posts.filter(post => {
      if (!post.scheduled_for) return false;
      const postDate = new Date(post.scheduled_for);
      return isSameDay(postDate, date) && postDate.getHours() === hour;
    });
  };

  // Helper function to check if post has recurrence rule
  const hasRecurrence = (post: SocialPost): boolean => {
    return !!post.recurrence_rule;
  };

  // Render week view
  const renderWeekView = () => {
    const days = [];
    const dateFormat = "EEE d";
    const startDate = currentWeek;
    const timeSlots = Array.from({ length: 24 }, (_, i) => i);
    
    // Create column headers (days of week)
    const dayHeaders = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(startDate, i);
      const formattedDate = format(day, dateFormat);
      const isToday = isSameDay(day, new Date());
      
      dayHeaders.push(
        <div 
          key={`header-${i}`} 
          className={`p-2 text-center border-b ${isToday ? 'bg-blue-100 font-bold' : ''}`}
        >
          {formattedDate}
        </div>
      );
    }
    
    // Create time slots
    const rows = timeSlots.map(hour => {
      const hourCells = [];
      
      // Add time label
      hourCells.push(
        <div key={`time-${hour}`} className="p-2 text-right text-sm text-gray-500 border-r">
          {format(new Date().setHours(hour, 0, 0, 0), 'h a')}
        </div>
      );
      
      // Add cells for each day
      for (let i = 0; i < 7; i++) {
        const day = addDays(startDate, i);
        const hourPosts = getPostsForHour(day, hour);
        const isBusinessHour = hour >= 9 && hour <= 17;
        
        hourCells.push(
          <div 
            key={`day-${i}-hour-${hour}`} 
            className={`p-1 border-b border-r min-h-[60px] ${isBusinessHour ? 'bg-blue-50' : ''} ${isDragging ? 'drop-target hover:bg-blue-100' : ''}`}
            onDragOver={handleDragOver(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour))}
            onDrop={handleDrop(new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour))}
          >
            {hourPosts.length > 0 ? (
              <div className="space-y-1">
                {hourPosts.map(post => (
                  <div 
                    key={post.id} 
                    className={`p-1 rounded text-xs shadow-sm cursor-move ${draggedPost?.id === post.id ? 'opacity-50' : ''}`}
                    style={{ 
                      backgroundColor: post.color || '#FFFFFF',
                      borderLeft: `4px solid ${post.color ? darkenColor(post.color, 20) : '#3B82F6'}`,
                      color: post.color ? getContrastTextColor(post.color) : 'inherit'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditPost(post);
                    }}
                    draggable={true}
                    onDragStart={handleDragStart(post)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex space-x-1">
                        {post.platforms?.map((platform, idx) => (
                          <span key={idx} className="scale-75"><PlatformIcon platform={platform} size="sm" /></span>
                        ))}
                        {hasRecurrence(post) && (
                          <span title="Recurring post" className="text-purple-500 text-xs">↻</span>
                        )}
                      </div>
                    </div>
                    <p className="truncate">{post.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`h-full w-full text-center text-xs text-gray-400 ${isDragging ? 'bg-blue-50 border-blue-200 border border-dashed' : ''}`}>
                {isDragging ? 'Drop to schedule here' : ''}
              </div>
            )}
          </div>
        );
      }
      
      return (
        <div key={`row-${hour}`} className="grid grid-cols-8">
          {hourCells}
        </div>
      );
    });
    
    return (
      <div className="bg-white rounded shadow overflow-hidden">
        <div className="grid grid-cols-8">
          <div key="corner" className="border-b border-r p-2"></div>
          {dayHeaders}
        </div>
        <div className="overflow-y-auto max-h-[700px]">
          {rows}
        </div>
      </div>
    );
  };

  // Render day view
  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <div className="bg-white rounded shadow overflow-hidden">
        <div className="grid grid-cols-1 divide-y divide-gray-200">
          {hours.map((hour) => {
            const hourPosts = getPostsForHour(currentDay, hour);
            const hourLabel = format(new Date().setHours(hour, 0, 0, 0), 'h a');
            
            return (
              <div 
                key={hour} 
                className={`min-h-[80px] p-2 ${hour >= 9 && hour <= 17 ? 'bg-blue-50' : 'bg-white'}`}
              >
                <div className="flex items-start">
                  <div className="w-16 flex-shrink-0 font-medium text-gray-500">
                    {hourLabel}
                  </div>
                  <div 
                    className="flex-grow"
                    onDragOver={handleDragOver(new Date(currentDay.setHours(hour, 0, 0, 0)))}
                    onDrop={handleDrop(new Date(currentDay.setHours(hour, 0, 0, 0)))}
                  >
                    {hourPosts.length === 0 ? (
                      <div className={`h-full w-full border border-dashed ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded p-2 text-sm text-gray-400`}>
                        {isDragging ? 'Drop to schedule here' : 'No posts scheduled'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {hourPosts.map(post => (
                          <div 
                            key={post.id} 
                            className={`p-2 rounded shadow-sm cursor-move ${draggedPost?.id === post.id ? 'opacity-50' : ''}`}
                            style={{ 
                              backgroundColor: post.color || '#FFFFFF',
                              borderLeft: `4px solid ${post.color ? darkenColor(post.color, 20) : '#3B82F6'}`,
                              color: post.color ? getContrastTextColor(post.color) : 'inherit'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditPost(post);
                            }}
                            draggable={true}
                            onDragStart={handleDragStart(post)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex space-x-1">
                                {post.platforms?.map((platform, idx) => (
                                  <span key={idx}><PlatformIcon platform={platform} /></span>
                                ))}
                              </div>
                              <span className="text-xs text-gray-500">
                                {post.scheduled_for && format(new Date(post.scheduled_for), 'h:mm a')}
                              </span>
                            </div>
                            <p className="mt-1 text-sm truncate">{post.content}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render cells
  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = new Date(day);
        const datePosts = getPostsForDate(cloneDay);
        const formattedDate = format(day, 'd');
        const isCurrentMonth = isSameMonth(day, monthStart);
        
        days.push(
          <div
            key={day.toString()}
            className={`min-h-[100px] p-2 border ${
              !isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'
            } ${isSameDay(day, selectedDate) ? 'bg-blue-50 border-blue-300' : ''}
            ${isDragging ? 'drop-target' : ''}`}
            onClick={() => onSelectDate(cloneDay)}
            onDragOver={handleDragOver(cloneDay)}
            onDrop={handleDrop(cloneDay)}
          >
            <div className="flex justify-between mb-1">
              <span className={`text-sm font-medium ${isSameDay(day, new Date()) ? 'bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : ''}`}>
                {formattedDate}
              </span>
              {datePosts.length > 0 && (
                <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded-full">
                  {datePosts.length}
                </span>
              )}
            </div>
            <div className="space-y-1">
              {datePosts.slice(0, 2).map((post) => (
                <div 
                  key={post.id} 
                  className={`text-xs p-1 truncate rounded cursor-move hover:opacity-90 ${draggedPost?.id === post.id ? 'opacity-50' : ''}`}
                  style={{ 
                    backgroundColor: post.color || '#E5E7EB',
                    borderLeft: `3px solid ${post.color ? darkenColor(post.color, 20) : '#9CA3AF'}`,
                    color: post.color ? getContrastTextColor(post.color) : 'inherit'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPost(post);
                  }}
                  draggable={true}
                  onDragStart={handleDragStart(post)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex space-x-1">
                    {post.platforms.map((platform, idx) => (
                      <span key={idx}><PlatformIcon platform={platform} /></span>
                    ))}
                  </div>
                  <div className="truncate">{post.content}</div>
                </div>
              ))}
              {datePosts.length > 2 && (
                <div className="text-xs text-gray-500">
                  +{datePosts.length - 2} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }

      rows.push(
        <div key={day.toString()} className="grid grid-cols-7 gap-2">
          {days}
        </div>
      );
      days = [];
    }

    return <div className="space-y-2">{rows}</div>;
  };
  
  // Render selected date posts
  const renderSelectedDatePosts = () => {
    if (selectedDatePosts.length === 0) {
      return (
        <div className="bg-white p-4 rounded shadow-sm mb-4">
          <p className="text-gray-500">No posts scheduled for {format(selectedDate, 'MMM d, yyyy')}</p>
        </div>
      );
    }

    return (
      <div className="bg-white p-4 rounded shadow-sm mb-4">
        <h3 className="font-medium text-lg mb-2">Posts for {format(selectedDate, 'MMM d, yyyy')}</h3>
        <div className="space-y-3">
          {selectedDatePosts.map((post) => (
            <div key={post.id} className="border rounded p-3 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex space-x-2">
                  {post.platforms.map((platform, idx) => (
                    <span key={idx}><PlatformIcon platform={platform} /></span>
                  ))}
                  {post.status && formatStatus(post.status)}
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => onEditPost(post)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => window.confirm('Are you sure you want to delete this post?') && onDeletePost(post.id || '')}
                    className="text-red-500 hover:text-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
              <p className="text-sm">{post.content}</p>
              {post.scheduled_for && (
                <p className="text-xs text-gray-500 mt-2">
                  Scheduled for: {format(new Date(post.scheduled_for), 'h:mm a')}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Render current view based on viewMode
  const renderCurrentView = () => {
    switch (viewMode) {
      case 'day':
        return renderDayView();
      case 'week':
        return renderWeekView();
      case 'month':
      default:
        return (
          <>
            {renderDays()}
            {renderCells()}
          </>
        );
    }
  };

  return (
    <div className="calendar">
      {renderHeader()}
      {renderCurrentView()}
      
      {/* Show selected date posts information (only in month view) */}
      {viewMode === 'month' && renderSelectedDatePosts()}
    </div>
  );
};

export default CalendarView;
